import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { siteImages } from '../data/mock';
import { Target, Users, Award, Heart, Wrench, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  useEffect(() => {
    document.title = 'About Us | EC Tool Works';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />

      <main className="">
        {/* Banner Section */}
        <section className="relative h-64 md:h-80 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={siteImages.aboutBanner}
              alt="About Us"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/40" />
          </div>
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-full text-orange-700 text-xs font-semibold mb-4">
                <Wrench className="w-3.5 h-3.5" />
                OUR STORY
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-gray-900 font-extrabold mb-3">
                About EC Tool Works
              </h1>
              <p className="text-gray-600 max-w-lg">
                Reliable hardware tools and workshop gear for contractors, electricians and serious DIYers
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Mission Section */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <span className="inline-block px-4 py-1.5 bg-orange-50 text-orange-700 text-xs font-bold uppercase tracking-wider rounded-full mb-4 border border-orange-100">
                Our Mission
              </span>
              <h2 className="font-display text-3xl lg:text-4xl text-gray-900 font-extrabold mb-6">
                Empowering Tradespeople to <span className="text-orange-600">Build Better</span>
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                At EC Tool Works, we believe that the right tool can make any job faster, safer and more accurate. Our mission is to put pro-grade hardware in the hands of every contractor, electrician, plumber and weekend builder &mdash; without the warehouse-store runaround.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                We carefully select each tool in our catalog, ensuring it meets our high standards for strength, precision and durability. Whether you're wiring a new circuit, framing a shop, drilling tile or hauling a kit to a job site, we have the hardware to back you up.
              </p>
              <div className="flex flex-wrap gap-4">
                {['Quality First', 'Trade Trusted', 'Precision Built'].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-xl border border-gray-200">
                    <CheckCircle2 className="w-4 h-4 text-orange-600" />
                    <span className="text-gray-900 text-sm font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden border border-gray-200">
                <img
                  src={siteImages.heroLifestyle}
                  alt="Hardware workshop"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-orange-600 text-white px-6 py-4 rounded-2xl shadow-lg">
                <p className="font-display font-extrabold text-2xl">14+</p>
                <p className="text-sm font-semibold">Hardware Tools</p>
              </div>
            </div>
          </div>

          {/* Core Values */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-orange-50 text-orange-700 text-xs font-bold uppercase tracking-wider rounded-full mb-4 border border-orange-100">
                Core Values
              </span>
              <h2 className="font-display text-3xl lg:text-4xl text-gray-900 font-extrabold">
                What Drives Us
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Target, title: 'Quality First', desc: 'Every tool is carefully selected for strength, precision and durability' },
                { icon: Users, title: 'Trade Focus', desc: 'Built for the people who use these tools day in and day out on the job' },
                { icon: Award, title: 'Excellence', desc: 'We hold our hardware to the standards a contractor would on their own bench' },
                { icon: Heart, title: 'Passion', desc: 'We love good tools and the projects they make possible' },
              ].map((value, i) => (
                <div key={i} className="bg-gray-50 border border-gray-200 rounded-2xl p-6 hover:border-orange-300 transition-all group">
                  <div className="w-14 h-14 bg-orange-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-orange-100 transition-colors">
                    <value.icon className="w-7 h-7 text-orange-700" />
                  </div>
                  <h3 className="font-display font-bold text-gray-900 text-lg mb-2">{value.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Why Choose Us */}
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8 md:p-12">
            <div className="text-center mb-10">
              <h2 className="font-display text-3xl lg:text-4xl text-gray-900 font-extrabold mb-4">
                Why Choose EC Tool Works?
              </h2>
              <p className="text-gray-500 max-w-2xl mx-auto">
                We're committed to giving you the best hardware tools and customer experience &mdash; every order, every time
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                'Pro-grade hardware tools and workshop gear',
                'Fast shipping across the USA',
                'Secure checkout with SSL encryption',
                '30-day hassle-free returns',
                'Dedicated customer support',
                'Carefully curated product selection',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-4 bg-gray-100 rounded-xl">
                  <CheckCircle2 className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <span className="text-gray-900 font-medium">{item}</span>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 px-8 py-4 bg-orange-600 text-white font-bold uppercase tracking-wider rounded-xl hover:bg-orange-500 transition-all shadow-lg"
              >
                Shop Our Hardware
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
